import { Country, RegUser, TermInsuranceProduct } from '@common/types';
import { Dayjs } from '@ui';

export enum QuoteType {
  Normal = 'normal',
  Fast = 'fast'
}

export enum DiscountOn {
  Base = 'base',
  Total = 'total'
}

export enum BoundaryDependency {
  // These should match the possible values in the dependencies field in the product boundaries API
  NRI = 'is_nri'
}

export interface UserSliceState {
  /**
   * User is using normal quote or fast quote
   */
  quote: QuoteType;
  /**
   * To track the current step the user is on
   */
  step: number;
  /**
   * Calculate the progress whenever step changes
   */
  stepProgress: number;
  /**
   * User quote details
   */
  plan: TermQuotePlanDetails;
  /**
   * Derived props from plan to avoid recalculation every time
   */
  derived: DerivedPlanDetails;
  /**
   * Current premium for the plan
   */
  premium: Premium;
  /**
   * Loading progress for premium API requests
   */
  premiumApiProgress: number;
  /**
   * premium fetched or not
   */
  premiumFetched: boolean;
  /**
   * Loading state for suggestion API requests
   */
  loadingSuggestion: boolean;
  /**
   * Whether suggestion API has been called and we have a response
   */
  suggestionFetched: boolean;
  /**
   * Suggestion API error
   */
  suggestionError: string;
  /**
   * Loading state for estimate API requests
   */
  loadingEstimate: boolean;
  /**
   * Estimate fetched or not
   */
  estimateFetched: boolean;
  /**
   * Suggestion/estimate received
   */
  suggestion: Suggestion | null;
  /**
   * List of add ons user can opt for with premium
   */
  addOns: TermAddOns;
  /**
   * To keep track of the current addon user has selected
   */
  currentAddOn: string;
  /**
   * Loading state for addons API requests
   */
  loadingAddOns: boolean;
  /**
   * Loading state for premium API requests
   */
  loadingPremium: boolean;
  /**
   * The policy details generated once quote is finalized
   */
  premiumOverlap: boolean;
  disableWopMaxLife: boolean;
  policy: any;
  isNRIEnabled: boolean;
  planFormValid: boolean;
  /*  
    In case of products that are only for existing users (e.g. MLI STEP),
    users have to verify their policy to buy the product.
  */
  policyVerification?: {
    oldPolicyNumber?: string;
    // Once the policy is verified, the API returns the following
    // otp_id - UUID from successful policy verification
    otpId?: string;
    // country_code - verified country code from policy verification
    countryCode?: string;
    // phone_num - verified phone number from policy verification
    phoneNum?: string;
    // cooldownUntil - timestamp until when policy verification OTP cannot be resent (cooldown period) set to 30 seconds
    cooldownUntil?: number;
  };
  setPlanFormValid: (isValid: boolean) => void;
  clearSuggestion: () => void;
  setAboutUserFields: (aboutDetails: AboutUserFields) => Promise<void>;
  setLifeStyleUserFields: (lifeStyleDetails: LifeStyleFields) => void;
  updateUserCover: (latestCover: number) => void;
  updateUserCoverTill: (latestCoverTill: number) => void;
  updateUserPaymentFreq: (latestPaymentFreq: PaymentFrequency) => void;
  getCoverSuggestion: () => Promise<void>;
  getEstimate: (
    premiumChangeInfo?: boolean,
    retainFeatures?: boolean
  ) => Promise<void>;
  setUserDetails: (latestUserDetails: TermEssentialUserDetails) => void;
  setPlanDetails: (latestPlanDetails: TermEssentialPlanDetails) => void;
  setStep: (latestStep: number) => void;
  setUserProduct: (latestProduct: string) => void;
  setUserProductVariant: (variant?: string) => void;
  setFastQuote: (latestProduct: string) => void;
  setUserPayOption: (latestPayOption: PaymentTermOption | null) => Promise<void>;
  getAddOnsForPlan: () => Promise<void>;
  getPremium: () => Promise<void>;
  toggleAddOn: (addOnKey: string, included: boolean) => void;
  modifyAddOnCover: (
    addOnKey: string,
    cover: number,
    variant?: VariantSelection
  ) => void;
  selectNextAddOn: () => void;
  selectPrevAddOn: () => void;
  finalizeTermQuote: (user: RegUser) => Promise<void>;
  resetCurrentAddOn: () => void;
  resetAddOnPremium: (addOn: TermAddOn) => void;
  setCurrentAddOn: (addOnKey: string) => void;
  resetPlan: () => void;
  setWopStatusForMaxLife: (value: boolean) => void;
  setPolicyVerificationCooldown: (cooldownUntil?: number) => void;
  sendPolicyOTP: (
    policyNumber: string,
    turnstileToken: string
  ) => Promise<PolicyVerificationResult>;
  verifyPolicyOTP: (otp: string) => Promise<PolicyVerificationResult>;
  clearPolicy: () => void;
}

export interface PolicyVerificationResult {
  success: boolean;
  message: string;
}

export interface ProductSliceState {
  /**
   * Loading state for boundaries API requests
   */
  loadingProductBoundaries: boolean;
  /**
   * Error message incase boundaries API fails
   */
  productBoundariesError: string;
  /**
   * Loading state for features requests
   */
  loadingProductFeatures: boolean;
  /**
   * Product details list with product details, boundaries, frequencies
   */
  productsList: TermProductDetails[];
  /**
   * List of payment term options applicable for the current product selected
   */
  paymentTermOptions: PaymentTermOption[];
  /**
   * Loading state for payment options requests
   */
  loadingPaymentTermOptions: boolean;
  fetchPaymentTermOptions: () => Promise<void>;
  fetchProductBoundaries: (
    products: string[],
    retainFeatures?: boolean
  ) => Promise<void>;
  refetchProductBoundaries: (
    boundaryDependency: BoundaryDependency
  ) => Promise<void>;
  fetchProductFeatures: (products: string[]) => Promise<void>;
  clearPaymentTermOptions: () => void;
}

export interface LayoutSliceState {
  // flag to show/hide user details modal
  userDetailsModal: boolean;
  // flag to show/hide plan details modal
  planDetailsModal: boolean;
  // premium change modal/toast related variables
  premiumChangeInfoType: string | null;
  premiumChangeTitle: string;
  premiumChangeDesc: string;
  showUserDetailsModal: () => void;
  hideUserDetailsModal: () => void;
  showPlanDetailsModal: () => void;
  hidePlanDetailsModal: () => void;
  showPremiumChangeModal: (premium: number) => void;
  hidePremiumChangeModal: () => void;
}

export type TermQuoteStoreState = UserSliceState &
  ProductSliceState &
  LayoutSliceState;

export interface NRIUserDetails {
  // is user an NRI
  isNRI: boolean | undefined;
  // country where user is residing
  nriCountry: string | undefined;
  // whether user wants to waive off GST, this is only applicable for NRI users
  gstWaiver: boolean | undefined;
}

export interface AboutUserFields extends NRIUserDetails {
  fullName: string;
  mobile: string;
  dob: Dayjs | null;
  gender?: string;
  tobacco?: boolean;
  diabetic?: boolean;
  is_nri?: boolean;
  gst_waiver?: boolean;
}

export interface LifeStyleFields {
  pincode?: number;
  education?: string;
  salaried?: boolean;
  annInc?: number;
  mExpenses?: number;
  existingCustomer?: boolean;
  // Existing user in our platform
  isExistingUser?: boolean;
  firstTimeBuyer?: boolean;
}

export type PaymentFrequency = 'Monthly' | 'Yearly' | 'Quarterly' | 'Half-Yearly';

export interface UserSelections {
  product: string | '';
  productVariant?: string;
  cover: number;
  tillAge: number;
  paymentFreq: PaymentFrequency;
  payOption: string;
}

export interface DerivedPlanDetails {
  age: number;
  payOptionItem: PaymentTermOption | undefined;
}

export interface TermQuotePlanDetails
  extends AboutUserFields,
    LifeStyleFields,
    UserSelections {}

export interface TermProduct {
  key: string; // product key
  name: string; // product name
  displayProductName: string; // product name should be shown in cards and policy pages
  insurerKey: string;
  insurerName: string;
  logo: string;
  uin: string; // uin number
  brochure: string; // brochure link
  policyWording?: string; // policy wording link
  csr: number; // claim settlement ratio
  tcs: number; // total customers served
  tas: number; // total amount settled
  asr: number; // amount settlement ratio
  voc: number; // volume of complaints
  usp: string;
}

export interface ProductVariant {
  key: string; // variant key
  label: string; // label for ui
  desc: string; // description for ui
  // cover restrictions
  cover: {
    min: number;
    max: number;
  };
  // age restrictions
  coverTill: {
    min: number;
    max: number;
  };
  // payment term options available for the the variant
  allowedPayoptions: PaymentTermOption[];
}

export interface Discount {
  /**
   * Any discount available for the product at the moment
   */
  discountText: string;
  /**
   * Type of discount currently available for the product
   */
  discountType: string;
  /**
   * Whether discount is given on base premium or total premium
   */
  discountOn: DiscountOn;
  /**
   * Discount info shown in premium cards
   */
  discountMsg: string;
}

export interface TermProductBoundary {
  /**
   * disable other genders for this product
   */
  disableOtherGender?: boolean;
  /**
   * for this product is pincode mandatory
   */
  pincodeRequired?: boolean;
  /**
   * to determine if we should check if the user is first time buyer of an individual life insurance policy
   */
  checkIfFirstTimeBuyer?: boolean;
  /**
   * to determine if we should check if the user is an existing customer of the insurer
   */
  checkIfExistingCustomer?: boolean;

  /**
   * Whether the product is available for existing Ditto users only.
   * This is different from checkIfExistingCustomer, it can be any insurer / product.
   * If true, then isExistingUser field will be shown in the lifestyle details step. Selection will be sent to the backend as is_existing_user. Form field is isExistingUser.
   */
  existingUsersOnly?: boolean;

  /* Alternate product slug */
  alternateProduct?: {
    productVerboseName: string;
    productKey: TermInsuranceProduct;
  };
  /**
   * Text used to show in the popup for policies for which pincode is required
   * Current : The communication address is where you reside currently. It's where the insurer will conduct your medicals. Also do note that ICICI | Maxlife doesn't service a few pincodes due to Covid related restrictions.
   */
  pincodeInfo?: string;

  /**
   * Smoke duration for the product, if smoked within this period user has to pay higher premium default 24 months
   */
  smokeDuration?: number;
  /**
   * Minimum age to be eligible for the policy
   */
  minAge: number;
  /**
   * maximum age to be eligible for the policy
   */
  maxAge: number;
  /**
   * minimum income required to be eligible for the policy
   */
  minInc: number;
  /**
   * is the policy available for diabetic patients
   */
  allowedDiabetic: boolean;

  /**
   * Array of discounts
   */
  discounts: Discount[];
  /**
   * minimum and maximum cover allowed
   */
  cover: {
    minAllowed: number;
    maxAllowed: number;
  };
  /**
   * Min and max till age
   */
  coverTill: {
    min: number;
    max: number;
  };
  /**
   * Optional custom list of Education qualification options for the product.
   *
   * Eg: Diploma is not supposed to be shown for MLI STEP.
   */
  educationOptions?: string[];

  /** Should prevent rider premiums becoming more than base premium */
  preventRiderPremiumOverlap: boolean;

  cardFeatures: TermProductCardFeature[];
  /**
   * Most of the cases these are free addons present in the policy
   */
  features: TermProductFeature[];

  mainBenefits: TermProductMainBenefits[];
  // Info for NRI users
  nriInfo?: string;

  // Countries that the Term product is available at. This is only applicable for NRI users.
  countries?: Country[];

  // Whether NRI is enabled for the product.
  isNRIEnabled: boolean;

  // Whether GST waiver option is available for NRI users for the product.
  enableGSTWaiverForNRI: boolean;
  // Whether premium includes GST - used to show TermIssuanceDelayForNoGST
  premiumWithGST: boolean;

  // boundaries are dependent on these parameters
  dependencies?: BoundaryDependency[];

  // Only Indian and NRI Country phone numbers are allowed for this product
  // Other countries are restricted
  shouldRestrictLoginPhone?: boolean;
  /**
   * Additional variants of the same product they may have different cover and till age restrictions
   * They may be available for certain payoptions too
   */
  additionalVariants?: ProductVariant[];

  notCovered?: TermProductMissingFeature[];
}

export interface TermProductFeature {
  icon: string;
  title: string;
  desc: string;
  // show the feature only if optional addon is selected
  optionalAddOnKey?: string;
}
export interface TermProductCardFeature {
  icon: string;
  text: string;
}

export interface TermProductMainBenefits {
  value: string;
  title: string;
  desc: string;
  icon: string;
  hint?: string;
}

export interface TermProductMissingFeature {
  text: string;
}

export interface TermProductDetails {
  product: TermProduct;
  boundaries: TermProductBoundary;
  paymentFrequencies: string[];
}

export interface TermEssentialUserDetails extends NRIUserDetails {
  dob: Dayjs | null;
  gender: string;
  tobacco: boolean;
  diabetic: boolean;
}

export interface TermEssentialPlanDetails {
  cover: number;
  tillAge: number;
  paymentFreq: PaymentFrequency;
}

export interface PaymentTermOption {
  key: string;
  name: string;
  description: string;
  paymentTerm: number;
  total: {
    base: number;
    tax: number;
  };
  savings: number;
  recommended: boolean;
}

export interface CustomPaymentTermOption {
  name: string;
  paymentTerm: number;
}

export interface SuggestedProduct {
  // unique key for the product
  key: string;
  // whether the premium was retrieved successfully
  success: true;
  // whether the product is available to the user
  available: true;
  // base premium
  basePlan: {
    base: number;
    tax: number;
  };
  // till what age the product is available
  tillAge: number;
  // product meta
  meta: {
    // is user eligible
    isEligible: boolean;
    // max allowed cover user can opt
    maxAllowedCover: number;
    minAllowedCover: number;
    physicalMedRequired: boolean;
    teleVideoAccepted: boolean;
    // is the policy available for the selected pincode
    pincodeAvailability: {
      available: boolean;
      type: string;
      category: string;
    };
  };
  secondYearPremium: {
    baseWithTax: number;
    base: number;
    tax: number;
  };
  firstYearDiscount: number;
}

export interface SuggestedBetterOption {
  message: string;
  link: string;
}

export interface Suggestion {
  // suggested cover for the user based on their plan
  suggestedCover: number;
  // till what age they will have the cover for
  coverTill: number;
  // maxAllowed cover for the current user
  maxAllowed: number;
  // which is the recommended product among the products
  recommended: string;
  // List of products suggested
  products: SuggestedProduct[];
  // if there is a better option than the selected product in case of fast quote
  betterOption?: SuggestedBetterOption;
}

export interface Premium {
  // how long policy is available for
  policyTerm?: number;
  // Base plan premium
  basePlan: {
    base: number;
    tax: number;
  };
  // if plan has discount what is the basePlan total without discount and the discount
  basePlanActual?: {
    basePlan: number;
    discount: number;
  };
  // adhb addon additional premium
  adhb: {
    base: number;
    tax: number;
  };
  // acb addon additional premium
  acb: {
    base: number;
    tax: number;
  };
  // ci addon additional premium
  ci: {
    base: number;
    tax: number;
  };
  // ib addon additional premium
  ib?: {
    base: number;
    tax: number;
  };
  // wop addon additional premium
  wop?: {
    base: number;
    tax: number;
  };
  // total premium, base  + included addons premium
  total: {
    base: number;
    tax: number;
  };
  tillAge?: number;
  // whether discount is included
  discountIncluded?: boolean;
  // payment frequency
  freq?: string;
  //second year premium
  secondYearPremium: { baseWithTax: number; base: number; tax: number };
  firstYearDiscount: number;
}

export interface VariantSelection {
  // Currently selected addon variant type
  variant?: string;
  // tpd is enabled or not
  tpd?: boolean;
}

export interface TermAddOnVariant {
  /**
   * Name of the variant
   */
  name: string;
  /**
   * Type of the variant, should be unique
   */
  type: string;
  /**
   * Tpd should be enabled or not
   */
  enableTpd?: boolean;
  /**
   * Variant specific limits
   */
  limits: {
    min: number;
    max: number;
    step: number;
  };
  /**
   * Policy term for the variant
   */
  policyTerm: number;
  /**
   * Premium for this variant should not exceed base premium
   */
  preventOverflow: boolean;

  /**
   * For addon variants under CI we have to show the list of illness
   * for which the user gets covered
   */
  illnessInfo?: {
    label: string;
    title: string;
    illnessList: [string];
  };
}

export interface TermAddOn {
  /**
   * Unique key for the addon
   */
  key: string;
  /**
   * Whether user is eligible for the addon
   */
  isEligible: boolean;
  /**
   * Why user is not eligible, applicable when isEligible is false
   */
  reason: string;
  /**
   * Name of the addon which can be shown to user
   */
  name: string;
  /**
   * Description based on plan variables, addon values
   */
  desc?: string;
  /**
   * Whether the addon is free or not
   */
  isFree: boolean;
  /**
   * Whether the free addon is optional or not
   */
  isOptional: boolean;
  /**
   * If the addon has cover enabled then the limits for slider
   */
  limits?: {
    min: number;
    max: number;
    step: number;
  };
  /**
   * What is the policy term for which the addon is available
   */
  policyTerm?: number;
  /**
   * Different variants available in the addon
   */
  variants?: [TermAddOnVariant];
  /**
   * Premium for this addon should not exceed base premium
   */
  preventOverflow?: boolean;
  /**
   * Early exit option limit
   */
  eeLimit?: number;

  /**
   * For few addons Like CI we have to show the list of illness
   * for which the user gets covered
   */
  illnessInfo?: {
    label: string;
    title: string;
    illnessList: [string];
  };

  // User selections section
  /**
   * Selected cover for the addon
   */
  cover: number;
  /**
   * Selected variant of the addOn
   */
  selectedVariant?: VariantSelection;
  /**
   * User has included the addon to their plan
   */
  included: boolean;
  /**
   * Premium for the addon
   */
  additionalPremium: {
    base: number;
    tax: number;
    term?: number;
  };
  /**
   * Whether the addon premium is included in base premium
   * Not free but base premium will be updated on inclusion of addon
   */
  inclusiveInBasePremium?: boolean;
  /**
   * Whether user has seen the addOn at least once
   */
  seen: boolean;
  /**
   * UIN for the rider
   */
  uin?: string;
  /**
   * Brochure for the rider
   */
  brochure?: string;
  /**
   * addOnTillAge for status page display
   */
  addOnTillAge?: string;
  /**
   * Type of the addon, whether it is free, recommended or other
   */
  type: TermAddOnType;
  /**
   * To show the addon amount  in status page
   */
  premium?: number;
  /**
   * To show the cover amount and till age  in status page
   */
  selectedOption?: string;
}

export type TermAddOns = TermAddOn[];

export enum TermAddOnType {
  FREE = 'free',
  RECOMMENDED = 'recommended',
  OTHER = 'other'
}
